import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient.js";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils.js";
import { getErrorMessage } from "#/utils/APIUtils.js";

const baseUrl = "/api/rkg/subjekt/status_reje";

/**
 * GET /page - Fetch paginated breeding statuses
 */
export const fetchBreedingStatusesPage = createAsyncThunk(
  "breedingStatuses/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        page,
        filter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /:id - Fetch breeding status by ID
 */
export const fetchBreedingStatusesById = createAsyncThunk(
  "breedingStatuses/byId",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * POST / - Create a new breeding status
 */
export const createBreedingStatus = createAsyncThunk(
  "breedingStatuses/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(baseUrl, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * PUT /:id - Update an existing breeding status
 */
export const updateBreedingStatus = createAsyncThunk(
  "breedingStatuses/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`${baseUrl}/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteBreedingStatus = createAsyncThunk(
  "breedingStatuses/delete",
  async (id, { rejectWithValue }) => {
    try {
      await ApiClient.delete(`${baseUrl}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const allThunks = [
  fetchBreedingStatusesPage,
  fetchBreedingStatusesById,
  createBreedingStatus,
  updateBreedingStatus,
  deleteBreedingStatus,
];

const breedingStatusesSlice = createSlice({
  name: "breedingStatuses",
  initialState: {
    page: {},
    isLoading: false,
    error: null,
    byId: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    /* PAGE */
    builder.addCase(fetchBreedingStatusesPage.fulfilled, (state, action) => {
      state.page = action.payload;
    });
    /* BY ID */
    builder.addCase(fetchBreedingStatusesById.fulfilled, (state, action) => {
      const status = action.payload;
      state.byId[status.id] = status;
    });
    /* CREATE */
    builder.addCase(createBreedingStatus.fulfilled, (state, action) => {
      const status = action.payload.output;
      state.byId[status.id] = status;
    });
    /* UPDATE */
    builder.addCase(updateBreedingStatus.fulfilled, (state, action) => {
      const status = action.payload.output;
      state.byId[status.id] = status;
    });
    /* DELETE */
    builder.addCase(deleteBreedingStatus.fulfilled, (state, action) => {
      const id = action.payload;
      delete state.byId[id];
    });

    handleLoadingAndErrorState(builder, allThunks);
  },
});

export default breedingStatusesSlice.reducer;
